import type { ApiResponse } from "src/api/common/apiTypes";
import type {
  MediaGroupListResponse,
  GetMediaListParams,
  MediaUploadResponse,
} from "src/types/storage";
import { axiosInstance } from "../axiosInstance";

const BASE_URL = "/v1/admin/storage/media";

// 목록 조회
export const getMediaListApi = async (
  params: GetMediaListParams
): Promise<ApiResponse<MediaGroupListResponse>> => {
  const res = await axiosInstance.get<ApiResponse<MediaGroupListResponse>>(
    BASE_URL,
    { params }
  );
  return res.data;
};

// 파일 업로드
export const uploadMediaApi = async (
  category: string,
  memo: string,
  files: File[]
): Promise<ApiResponse<MediaUploadResponse>> => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append("files", file);
  });

  const res = await axiosInstance.post<ApiResponse<MediaUploadResponse>>(
    BASE_URL,
    formData,
    {
      params: { category, memo },
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return res.data;
};

// 메모 수정
export const updateMediaMemoApi = async (
  fileGrpId: number,
  memo: string
): Promise<ApiResponse<void>> => {
  const res = await axiosInstance.patch<ApiResponse<void>>(
    `${BASE_URL}/${fileGrpId}/memo`,
    null,
    { params: { memo } }
  );
  return res.data;
};

// === 기존 Storage API 함수들 ===

type EditorImageUploadResponse = {
  imageUrl: string;
  originalName: string;
  fileSize: number;
};

export const uploadEditorImageApi = async (
  file: File
): Promise<ApiResponse<EditorImageUploadResponse>> => {
  const formData = new FormData();
  formData.append("file", file);

  const res = await axiosInstance.post<ApiResponse<EditorImageUploadResponse>>(
    "/v1/admin/storage/editor-image",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return res.data;
};

export type TempImageFile = {
  fileId: number;
  fileUrl: string;
  originalName: string;
  fileSize: number;
  fileOrder: number;
};

export type TempImageUploadResponse = {
  fileGrpId: number;
  files: TempImageFile[];
};

export const uploadTempImageApi = async (
  files: File[],
  fileGrpId?: number | null
): Promise<ApiResponse<TempImageUploadResponse>> => {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));

  const res = await axiosInstance.post<ApiResponse<TempImageUploadResponse>>(
    "/v1/admin/storage/temp",
    formData,
    {
      params: fileGrpId ? { fileGrpId } : undefined,
    }
  );
  return res.data;
};

export const deleteTempFileApi = async (fileId: number): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.delete<ApiResponse<null>>(
    `/v1/admin/storage/file/${fileId}`
  );
  return res.data;
};

// === 새로운 이미지 업로드 API ===

export type ImageFile = {
  fileId: number;
  fileUrl: string;
  originalName: string;
  fileSize: number;
  fileOrder: number;
};

export type ImageUploadResponse = {
  fileGrpId: number;
  files: ImageFile[];
};

/**
 * 이미지 업로드 API
 * @param directory - 업로드 디렉토리 (예: "brands/logos", "notice", "products/images/main")
 * @param files - 업로드할 파일 배열
 * @param fileGrpId - 기존 파일 그룹 ID (선택, 기존 그룹에 추가할 때 사용)
 * @param singleFile - 단일 파일 모드 (true: 1개만, false: 여러 개)
 */
export const uploadImageApi = async (
  directory: string,
  files: File[],
  fileGrpId?: number,
  singleFile: boolean = false
): Promise<ApiResponse<ImageUploadResponse>> => {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));

  const res = await axiosInstance.post<ApiResponse<ImageUploadResponse>>(
    "/v1/admin/storage/upload",
    formData,
    {
      params: {
        directory,
        ...(fileGrpId !== undefined && { fileGrpId }),
        singleFile,
      },
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return res.data;
};

/**
 * 파일 삭제 API (Soft Delete)
 * @param fileId - 삭제할 파일 ID
 */
export const deleteFileApi = async (fileId: number): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.delete<ApiResponse<null>>(
    `/v1/admin/storage/file/${fileId}`
  );
  return res.data;
};
